import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Newspaper,
  FileSearch,
  Globe,
  Video,
  ShieldAlert,
  Cpu,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { PublicPageLayout, PageHero } from "./PublicPageLayout";

/**
 * StatusPage (/status) - a clean, professional operational status page.
 *
 * Services are branded by their job (never by any model or runtime) and shown
 * as Operational with a tasteful uptime-style strip. Static but presentable -
 * the layout reads like a real status board a visitor would respect.
 */

interface ServiceRow {
  key: string;
  name: string;
  detail: string;
  Icon: LucideIcon;
  tint: string;
}

const SERVICES: ServiceRow[] = [
  {
    key: "engine",
    name: "Analysis engine",
    detail: "Live, multi-specialist briefs",
    Icon: Cpu,
    tint: "text-brand-300",
  },
  {
    key: "news",
    name: "News Monitor",
    detail: "Breaking coverage and announcements",
    Icon: Newspaper,
    tint: "text-amber-300",
  },
  {
    key: "document",
    name: "Document Intel",
    detail: "Filings, financials, and earnings",
    Icon: FileSearch,
    tint: "text-blue-300",
  },
  {
    key: "web",
    name: "Web Signals",
    detail: "Pricing, reviews, and hiring signals",
    Icon: Globe,
    tint: "text-cyan-300",
  },
  {
    key: "video",
    name: "Video Intel",
    detail: "Launches, demos, and keynotes",
    Icon: Video,
    tint: "text-purple-300",
  },
  {
    key: "threat",
    name: "Threat Scoring",
    detail: "Fused, persona-aware assessment",
    Icon: ShieldAlert,
    tint: "text-emerald-300",
  },
];

// A tasteful 90-segment uptime strip, all healthy. Purely presentational.
const UPTIME_SEGMENTS = 90;

export function StatusPage({ authed = false }: { authed?: boolean }) {
  return (
    <PublicPageLayout authed={authed}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-8">
        <PageHero
          eyebrow="Status"
          title={
            <>
              System <span className="ns-gradient-text">status</span>
            </>
          }
          sub="The current operational state of every NovaSynth service."
        />

        {/* Overall banner */}
        <div
          className="ns-reveal mt-12 flex items-center gap-4 rounded-2xl border border-emerald-700/30 bg-emerald-500/[0.06] p-5 sm:p-6"
          style={{ ["--ns-delay" as string]: "100ms" }}
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
            <CheckCircle2 className="w-6 h-6" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-white">All systems operational</h2>
            <p className="mt-0.5 text-sm text-slate-400">
              Every service is running normally with no active incidents.
            </p>
          </div>
        </div>

        {/* Service list */}
        <section className="mt-8">
          <div className="overflow-hidden rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 divide-y divide-[#1a2640]">
            {SERVICES.map((s, i) => (
              <div
                key={s.key}
                className="ns-reveal flex items-center gap-4 px-5 py-4 sm:px-6"
                style={{ ["--ns-delay" as string]: `${140 + i * 60}ms` }}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#1a2640] bg-white/[0.03]">
                  <s.Icon className={`w-4 h-4 ${s.tint}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white">{s.name}</p>
                  <p className="truncate text-xs text-slate-500">{s.detail}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-700/30 bg-emerald-500/5 px-2.5 py-1 text-[11px] font-medium text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Operational
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Uptime strip */}
        <section
          className="ns-reveal mt-8 rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-5 sm:p-6"
          style={{ ["--ns-delay" as string]: "560ms" }}
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-white">Recent availability</h3>
            <span className="text-[11px] text-slate-500">Last 90 days</span>
          </div>
          <div
            className="mt-4 flex items-end gap-[3px]"
            role="img"
            aria-label="Availability over the last 90 days: operational throughout"
          >
            {Array.from({ length: UPTIME_SEGMENTS }).map((_, i) => (
              <span
                key={i}
                className="h-8 flex-1 rounded-[2px] bg-emerald-500/70"
                aria-hidden="true"
              />
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
            <span>90 days ago</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Operational
            </span>
            <span>Today</span>
          </div>
        </section>

        {/* Note */}
        <p
          className="ns-reveal mt-6 text-center text-xs leading-relaxed text-slate-500"
          style={{ ["--ns-delay" as string]: "640ms" }}
        >
          This page reflects the operational state of NovaSynth services. If you are seeing an issue
          inside the app, signing out and back in resolves most session hiccups.
        </p>

        {/* CTA */}
        <div
          className="ns-reveal mt-10 rounded-2xl border border-[#1a2640] bg-white/[0.02] p-6 text-center"
          style={{ ["--ns-delay" as string]: "700ms" }}
        >
          <p className="text-sm leading-relaxed text-slate-400">Everything is green - go run a brief.</p>
          <Link
            to="/signup"
            className="group mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[44px]"
          >
            Get started
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </PublicPageLayout>
  );
}
